package org.example.project.components

import androidx.compose.foundation.layout.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch
import org.example.project.FirebaseManager
import org.example.project.PrimaryColor
import org.example.project.UseLicenseKeyResult

@Composable
fun LicenseKeySection() {
    val scope = rememberCoroutineScope()
    var licenseKeySn by remember { mutableStateOf("") }
    var licenseKeyPin by remember { mutableStateOf("") }
    var isClaimingKey by remember { mutableStateOf(false) }
    var licenseKeyMessage by remember { mutableStateOf<String?>(null) }
    var licenseKeySuccess by remember { mutableStateOf(false) }

    Card(
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(24.dp).fillMaxWidth()) {
            Text(
                text = "Apply License Key",
                style = MaterialTheme.typography.titleMedium,
                fontWeight = FontWeight.Bold,
                color = Color(0xFF64748B) // Slate 500
            )
            Spacer(modifier = Modifier.height(16.dp))
            
            OutlinedTextField(
                value = licenseKeySn,
                onValueChange = { licenseKeySn = it },
                label = { Text("Serial Number") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            Spacer(modifier = Modifier.height(12.dp))
            OutlinedTextField(
                value = licenseKeyPin,
                onValueChange = { licenseKeyPin = it },
                label = { Text("Security PIN") },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = PrimaryColor,
                    unfocusedBorderColor = Color(0xFFE2E8F0)
                )
            )
            Spacer(modifier = Modifier.height(16.dp))
            Button(
                onClick = { 
                    if (licenseKeySn.isNotBlank() && licenseKeyPin.isNotBlank()) {
                        scope.launch {
                            isClaimingKey = true
                            licenseKeyMessage = null
                            licenseKeySuccess = false
                            val result = FirebaseManager.useLicenseKey(licenseKeySn, licenseKeyPin)
                            when (result) {
                                is UseLicenseKeyResult.Success -> {
                                    licenseKeySuccess = true
                                    licenseKeyMessage = "Successfully applied! Added ${result.data.bonus_units} volume licenses to your organization."
                                    licenseKeySn = ""
                                    licenseKeyPin = ""
                                }
                                is UseLicenseKeyResult.Error -> {
                                    licenseKeyMessage = result.message
                                }
                            }
                            isClaimingKey = false
                        }
                    }
                },
                enabled = licenseKeySn.isNotBlank() && licenseKeyPin.isNotBlank() && !isClaimingKey,
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = PrimaryColor),
                shape = MaterialTheme.shapes.medium
            ) {
                if (isClaimingKey) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(20.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text("Apply", fontWeight = FontWeight.Bold)
                }
            }

            if (licenseKeyMessage != null) {
                Spacer(modifier = Modifier.height(16.dp))
                Surface(
                    color = if (licenseKeySuccess) Color(0xFFF0FDF4) else Color(0xFFFEF2F2),
                    shape = MaterialTheme.shapes.small,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.padding(16.dp),
                        verticalAlignment = androidx.compose.ui.Alignment.CenterVertically
                    ) {
                        Text(
                            text = licenseKeyMessage!!,
                            color = if (licenseKeySuccess) Color(0xFF16A34A) else Color(0xFFDC2626),
                            style = MaterialTheme.typography.bodySmall,
                            fontWeight = FontWeight.Medium
                        )
                    }
                }
            }
        }
    }
}
